import { lazy, Suspense, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { localStorageGet } from '@core/cookie';
import { Avatar, Spin, Tag, Typography, Popover, Tooltip } from 'antd';
import { expertActions } from '@/components/Expert/overview/store/expertSlice';
import styled from 'styled-components';
import { ExpertCredibilityBadge } from '@/components/common/ExpertCredibilityBadge';
import { expertLevels, credibilityList } from '@utils/constants';

export { ExpertCredibilityBadge } from '@/components/common/ExpertCredibilityBadge';
/** 动态加载，避免与 `Expert/profile`（其内引用 `ExpertCredibilityBadge`）形成循环依赖 */
const ExpertProfileDrawer = lazy(() => import('@/components/Expert/profile'));

const { Text } = Typography;

const StyledExpert = styled.div`
    display: inline-flex;
    align-items: center;
    position: relative;

    .expert__avatar {
        margin-right: 6px;
    }

    .expert__avatar__wrap {
        position: relative;
        display: inline-block;
        line-height: 0;
        vertical-align: middle;
    }

    .expert__avatar__gender {
        position: absolute;
        top: -2px;
        right: -2px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        // background: #fff;
        // box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
        font-size: 9px;
        line-height: 1;
        pointer-events: none;
        color: #fff;

        i.bi {
            font-size: 9px;
        }
    }

    .expert__avatar__gender--male {
        background-color: #1890ff;
    }

    .expert__avatar__gender--female {
        background-color: #eb2f96;
    }

    .expert__info__box {
        padding-right: 10px;
        // display: flex;
        // align-items: center;
        // flex-wrap: wrap;
        // gap: 4px;
    }

    .expert__info__name {
    }

    .expert__info__id {
        font-size: 12px;
        color: #888;
        font-weight: normal;
        white-space: nowrap;
        padding-left: 4px;
    }

    .expert__info__phone {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
        line-height: 1.5;
    }

    .expert__info__no_phone {
        margin-right: 0;
    }

    .expert__info__status {
        padding: 2px;
        font-size: 10px;
        line-height: 1em;
        margin-left: 4px;
        vertical-align: middle;
    }

    .expert__info__no_phone {
        padding: 2px;
        font-size: 10px;
        line-height: 1em;
        vertical-align: middle;
    }

    .expert__info__credibility {
        margin-top: 4px;
    }

    .expert__detail-btn {
        position: absolute;
        top: 7px;
        right: -6px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        height: 12px;
        color: #1890ff;
        font-size: 12px;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s;
        // border-radius: 4px;

        i.bi {
            font-size: 12px;
        }

        &:hover {
            background-color: #e6f4ff;
        }
    }

    &:hover .expert__detail-btn {
        opacity: 1;
    }
`;

/**
 * 专家信息卡片展示组件
 * @param {Object} expert - 专家数据对象
 * @param {string} expert.expertAvatar - 专家头像URL
 * @param {string} expert.memUsername - 专家姓名
 * @param {number} expert.memGender - 专家性别 (0: 女, 1: 男)
 * @param {number} expert.status - 专家状态 (1: 禁用, 0: 正常)
 * @param {number} expert.expertLevel - 专家等级
 * @param {string} expert.memMobile - 专家手机号
 * @param {string} expert.credibility - 专家可信度 (high/medium/low)
 * @param {Object} className - 可选的外部样式类名
 */
const { setCursor } = expertActions;

const ExpertInfoCard = ({ expert, className, showDetailButton = true }) => {
    const dispatch = useDispatch();
    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    const [detailExpertId, setDetailExpertId] = useState(null);

    const canViewEvalResult = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList?.length) {
            return roleList.some(item => item.name === '查看专家综评结果');
        }
        return false;
    }, []);

    const handleCloseProfileDrawer = () => {
        setProfileDrawerOpen(false);
        setDetailExpertId(null);
    };

    if (!expert) {
        return null;
    }

    const { expertAvatar, memUsername, memGender, status, expertLevel, memMobile, credibility } = expert;

    const expertId = expert.expertId ?? expert.id;
    const isClickable = !!expertId;

    const handleOpenExpertDetail = e => {
        e.stopPropagation();
        dispatch(setCursor(expertId));
        setDetailExpertId(expertId);
        setProfileDrawerOpen(true);
    };

    // 获取专家等级信息
    const levelInfo = expertLevels.find(a => a.level === expertLevel);
    // 获取可信度信息
    const credibilityInfo = credibilityList.find(a => a.value === credibility);

    return (
        <>
            <StyledExpert className={className}>
                <div className="expert__avatar">
                    <div className="expert__avatar__wrap">
                        {!!expertAvatar ? (
                            <Popover placement="right" trigger="hover" content={<img src={expertAvatar} style={{ width: 300 }} />}>
                                <Avatar src={expertAvatar} style={{ verticalAlign: 'middle' }} />
                            </Popover>
                        ) : (
                            <Avatar style={{ verticalAlign: 'middle' }}>
                                <i className="bi bi-person-fill" style={{ fontSize: 28 }}></i>
                            </Avatar>
                        )}
                        {memGender === 1 ? (
                            <span className="expert__avatar__gender expert__avatar__gender--male" aria-hidden>
                                <i className="bi bi-gender-male"></i>
                            </span>
                        ) : memGender === 0 ? (
                            <span className="expert__avatar__gender expert__avatar__gender--female" aria-hidden>
                                <i className="bi bi-gender-female"></i>
                            </span>
                        ) : null}
                    </div>
                </div>

                <div className="expert__info">
                    <div className="expert__info__box">
                        <span className="expert__info__name">{memUsername}</span>
                        <span className="expert__info__id">#{expertId}</span>
                        {status === 1 && (
                            <Tag className="expert__info__status" color="#f50">
                                禁用
                            </Tag>
                        )}
                    </div>

                    <div className="expert__info__level">{levelInfo && expertLevel >= 1 && expertLevel <= 6 ? <Text type="warning">{levelInfo.label}</Text> : levelInfo && <span>{levelInfo.label}</span>}</div>

                    <div className="expert__info__phone">
                        {!memMobile || memMobile?.startsWith('100') ? (
                            <Tag color="red" className="expert__info__no_phone">
                                无手机号
                            </Tag>
                        ) : (
                            memMobile
                        )}
                    </div>

                    {credibilityInfo && (
                        <div className="expert__info__credibility">
                            <ExpertCredibilityBadge credibility={credibility} />
                        </div>
                    )}
                </div>

                {showDetailButton && isClickable && (
                    <Tooltip title="查看专家详情" placement="right">
                        <span className="expert__detail-btn" onClick={handleOpenExpertDetail}>
                            <i className="bi bi-box-arrow-up-right"></i>
                        </span>
                    </Tooltip>
                )}
            </StyledExpert>

            {profileDrawerOpen && detailExpertId != null ? (
                <span onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                    <Suspense fallback={<Spin />}>
                        <ExpertProfileDrawer open expertId={detailExpertId} openInEditMode={false} onClose={handleCloseProfileDrawer} canViewEvaluation={canViewEvalResult} />
                    </Suspense>
                </span>
            ) : null}
        </>
    );
};

export default ExpertInfoCard;
